import type { Feature, Site } from "../types";
import { ACCESS_LABELS, LAYERS } from "../data/layers";
import { fmtDateRange } from "./format";

const esc = (s: unknown) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;" })[c] as string);

function describe(f: Feature): string {
  const parts: string[] = [];
  if (f.layer === "history") {
    const s = f.raw as Site;
    if (s.description) parts.push(s.description);
    const dr = fmtDateRange(s.date_start, s.date_end);
    if (dr) parts.push(dr);
    if (s.accessibility) parts.push(`access: ${ACCESS_LABELS[s.accessibility] ?? s.accessibility}`);
    if (s.parking?.notes) parts.push(`parking: ${s.parking.notes}`);
  } else {
    const r = f.raw as { summary?: string; town?: string };
    if (r.summary) parts.push(r.summary);
    if (f.sub) parts.push(f.sub);
    parts.push(LAYERS[f.layer].label);
  }
  return parts.join(" · ");
}

export function buildGpx(feats: Feature[]): string {
  const now = new Date().toISOString();
  const wpts = feats.map((f) => {
    const link = `${location.origin}${location.pathname}#sel=${encodeURIComponent(f.key)}`;
    let out = `  <wpt lat="${f.lat}" lon="${f.lon}">\n    <name>${esc(f.name)}</name>\n    <desc>${esc(describe(f))}</desc>\n    <link href="${esc(link)}"><text>${esc(f.name)}</text></link>\n    <sym>${f.layer === "history" ? "Historic Site" : "Waypoint"}</sym>\n  </wpt>\n`;
    if (f.layer === "history") {
      const p = (f.raw as Site).parking;
      if (p && typeof p.lat === "number" && typeof p.lon === "number") {
        out += `  <wpt lat="${p.lat}" lon="${p.lon}">\n    <name>${esc(f.name + " — parking")}</name>\n    <desc>${esc(p.notes ?? p.type ?? "")}</desc>\n    <sym>Parking Area</sym>\n  </wpt>\n`;
      }
    }
    return out;
  }).join("");
  return `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" creator="Ireland Travel Map" xmlns="http://www.topografix.com/GPX/1/1">\n  <metadata><name>Ireland Travel Map</name><time>${now}</time></metadata>\n${wpts}</gpx>\n`;
}

export function buildKml(feats: Feature[]): string {
  const pm = feats.map((f) => {
    const d = describe(f).replace(/]]>/g, "]]]]><![CDATA[>");
    return `  <Placemark>\n    <name>${esc(f.name)}</name>\n    <description><![CDATA[${d}]]></description>\n    <Point><coordinates>${f.lon},${f.lat},0</coordinates></Point>\n  </Placemark>\n`;
  }).join("");
  return `<?xml version="1.0" encoding="UTF-8"?>\n<kml xmlns="http://www.opengis.net/kml/2.2">\n<Document>\n  <name>Ireland Travel Map</name>\n${pm}</Document>\n</kml>\n`;
}

export function download(text: string, filename: string, mime: string): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function stamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
}
